import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { mapErrors } from "../../Utils";
import { fetchCustomer } from "../../../api/customer";
import { ArrowRightIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ProjectsStackList } from "../projects/projects-stack-list";
import { ServiceAgreementsStackList } from "../service/agreements/agreements-stack-list";
import { ReportsStackList } from "../service/reports/reports-stack-list";
import { OrdersStacklist } from "../orders/orders-stack-list";
import { CasesStackList } from "../cases/cases-stack-list";
import { TWclassNames } from "../../utils/Div";
import { PMDialog } from "../../utils/PMDialog";
import { CustomerEditForm } from "./customer-edit-form";
export default function CustomerDialog({
  selected = null,
  readOnly = false,
  open = false,
  selection = false,
  onClose = () => toast.error("Missing onClose function. Cannot close dialog"),
  redirect = false,
  ...props
}) {
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    customer: {},
  });

  // Deconstructing props
  const { onRefresh } = props;

  // Deconstruct itemState (Store)
  const { customer, isLoading, error, errors } = itemState;

  const [readOnlyState, setReadOnlyState] = useState(readOnly);

  const getCustomer = async () => {
    return fetchCustomer(selected.id)
      .then((res) => {
        if (res?.data?.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            customer: res?.data,
          }));
          // Ensure readOnly is true when loading existing customer
          setReadOnlyState(true);
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err?.data?.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          customer: {},
          error: true,
          errors: errors,
        }));
      });
  };



  const CustomerForm = () => null;

  useEffect(() => {
    if (open) {
      if (selected?.id) {
        // Set readOnly to true immediately when opening existing customer
        setReadOnlyState(true);
        getCustomer();
      } else {
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          customer: {},
          error: false,
          errors: [],
        }));

        setReadOnlyState(false); // Set readOnly to false when creating new customer
      }
    } else {
      // Reset readOnly when dialog closes
      setReadOnlyState(true);
    }
  }, [open, selected]);

  const [selectedTab, setSelectedTab] = useState(0);

  const handleRefresh = (data) => {
    setItemState((prevState) => ({
      ...prevState,
      customer: data,
      isLoading: false,
    }));
  };

  const handleLoading = () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
  };

  const handleLoadingEnd = () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: false,
    }));
  };

  const tabs = [
    {
      name: t("Details"),
      component: (
        <CustomerEditForm
          item={customer}
          readOnly={readOnlyState}
          onRefresh={(data) => handleRefresh(data)}
          onLoading={() => handleLoading()}
          onLoadingEnd={() => handleLoadingEnd()}
        />
      ),
    },
    {
      name: t("Cases"),
      component: (
        <CasesStackList
          module_param={customer.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: customer.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("module_overview.tab.orders"),
      component: (
        <OrdersStacklist
          module_param={customer.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: customer.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("module_overview.tab.reports"),
      component: (
        <ReportsStackList
          module_param={customer.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: customer.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("module_overview.tab.agreements"),
      component: (
        <ServiceAgreementsStackList
          module_param={customer.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: customer.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("module_overview.tab.projects"),
      href: "#",
      component: (
        <ProjectsStackList
          module_param={customer.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: customer.id,
            },
          ]}
        />
      ),
    },
  ];

  function customerTabs() {
    return (
      <div>
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            {t("module_overview.select_tab")}
          </label>
          {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
          <select
            onChange={(e) => setSelectedTab(e.target.value)}
            id="tabs"
            name="tabs"
            className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
            defaultValue={tabs[selectedTab].name}
          >
            {tabs.map((tab, tabIdx) => (
              <option key={tab.name} value={tabIdx}>
                {tab.name}
              </option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <nav
            className="isolate flex divide-x divide-gray-200 rounded-lg shadow"
            aria-label="Tabs"
          >
            {tabs.map((tab, tabIdx) => (
              <a
                key={tab.name}
                onClick={() => setSelectedTab(tabIdx)}
                className={TWclassNames(
                  tabIdx === selectedTab
                    ? "text-gray-900"
                    : "text-gray-500 hover:text-gray-700",
                  tabIdx === 0 ? "rounded-l-lg" : "",
                  tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                  "cursor-pointer group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10"
                )}
                aria-current={tabIdx === selectedTab ? "page" : undefined}
              >
                <span>{tab.name}</span>
                <span
                  aria-hidden="true"
                  className={TWclassNames(
                    tabIdx === selectedTab ? "bg-indigo-500" : "bg-transparent",
                    "absolute inset-x-0 bottom-0 h-0.5"
                  )}
                />
              </a>
            ))}
          </nav>
        </div>
      </div>
    );
  }

  const modalHeaderActions = [
    {
      label: t("module_overview.go_to_customer"),
      icon: ArrowRightIcon,
      path: `/dashboard/customers/` + customer?.id,
    },
    {
      label: readOnlyState ? t("edit") : t("read_only"),
      icon: readOnlyState ? XMarkIcon : PencilIcon,
      action: () => setReadOnlyState(!readOnlyState),
    },
  ];

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      headerActions={modalHeaderActions}
      isLoading={isLoading}
      title={
        readOnlyState
          ? customer?.id
            ? customer?.displayTitle
            : t("customer")
          : customer?.id
          ? customer?.displayTitle
          : t("customers.create")
      }
    >
      <>
        {customer?.id ? customerTabs() : null}
        <div className="py-5">{tabs[selectedTab].component}</div>
      </>
    </PMDialog>
  );
}

